import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LuPlus } from "react-icons/lu";
import { BeatLoader } from "react-spinners";

import { Table } from "../components/table/table";
import { IIndustry } from "../types";
import IndustryDropdownActions from "../components/actions/industry-dropdown";
import EditIndustryModal from "../components/modals/edit-industry";
import {
  deleteIndustryByAdmin,
  getAllIndustriesByAdmin,
} from "../services/industriesService";
import DeleteIndustryModal from "../components/modals/delete-industry";
import AddIndustryModal from "../components/modals/add-industry";

interface IndustriesProps {
  searchQuery?: string;
}

const Industries: React.FC<IndustriesProps> = ({ searchQuery }) => {
  const queryClient = useQueryClient();

  const {
    data: industries,
    isLoading,
    error,
  } = useQuery<IIndustry[]>({
    queryKey: ["industries", searchQuery],
    queryFn: () => getAllIndustriesByAdmin(searchQuery),
  });

  const [editIndustry, setEditIndustry] = useState<IIndustry | null>(null);
  const [deleteIndustryData, setDeleteIndustryData] =
    useState<IIndustry | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (industryId: string) => deleteIndustryByAdmin(industryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["industries"] });
      setDeleteIndustryData(null);
    },
  });

  const handleEdit = (industry: IIndustry) => {
    setEditIndustry(industry);
  };

  const handleDelete = (industry: IIndustry) => {
    setDeleteIndustryData(industry);
  };

  const confirmDelete = () => {
    if (deleteIndustryData) {
      deleteMutation.mutate(deleteIndustryData._id);
    }
  };

  const LuPlusIcon = LuPlus as React.ElementType;

  const columns = [
    {
      header: "Industry Name",
      accessorKey: "name",
      cell: ({ row }: { row: { original: IIndustry } }) => (
        <p className="text-sm font-medium text-gray-800">{row.original.name}</p>
      ),
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }: { row: { original: IIndustry } }) => (
        <p className="text-sm text-gray-600">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </p>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: IIndustry } }) => (
        <IndustryDropdownActions
          industry={row.original}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (error)
    return <p className="text-rose-500 text-sm">Error loading industries.</p>;

  return (
    <div className="">
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Industries</h2>
        <div
          onClick={() => setShowAddModal(true)}
          className="p-2 cursor-pointer bg-indigo-800 rounded-full flex items-center justify-center text-white hover:bg-opacity-50 transition"
        >
          <LuPlusIcon className="text-lg" />
        </div>
      </div>
      <Table columns={columns} data={industries || []} />

      {editIndustry && (
        <EditIndustryModal
          industry={editIndustry}
          onClose={() => setEditIndustry(null)}
        />
      )}
      {showAddModal && (
        <AddIndustryModal onClose={() => setShowAddModal(false)} />
      )}
      {deleteIndustryData && (
        <DeleteIndustryModal
          isOpen={!!deleteIndustryData}
          industryName={deleteIndustryData.name}
          onClose={() => setDeleteIndustryData(null)}
          onConfirm={confirmDelete}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default Industries;
